/**
 * Playback pipeline.
 *
 * Receives compressed Opus frames from the transport, decodes them on
 * the main thread (one [`OpusDecoder`] per active sender so per-sender
 * decoder state — packet history used for PLC and forward-error
 * correction — is preserved), and ships the resulting PCM to the
 * playback `AudioWorklet` which mixes and writes to the speakers.
 *
 * Decode runs on the main thread because:
 *  - The current Opus WASM module is not designed to be re-instantiated
 *    inside an AudioWorkletGlobalScope (Emscripten output assumes
 *    `window` / `WorkerGlobalScope` and uses `fetch`).
 *  - We are decoding at most K=3 streams of 16 kHz mono — well inside
 *    the main thread's budget. If this ever became a bottleneck we
 *    would move decode into a regular Worker; the worklet does not
 *    need to be the WASM host.
 */

import { OpusDecoder } from "./opus";

/** Constructor options for [`startPlayback`]. */
export interface PlaybackOptions {
    /**
     * Identifier of the output device to render through, as returned
     * by `navigator.mediaDevices.enumerateDevices()`. Honoured only on
     * browsers that implement `AudioContext.setSinkId` — currently
     * recent Chromium-based browsers. Safari/iOS picks the route via
     * the OS audio-route picker; on those browsers this option is
     * silently ignored. An unknown or stale id falls through to the
     * system default.
     */
    deviceId?: string;
    /**
     * Linear multiplier applied to the playback graph between the
     * mixer worklet and the output device. 1.0 is unity; values >1
     * boost (with eventual soft-clipping in the worklet); 0 mutes.
     * Useful when the OS keeps trimming system volume — the user can
     * compensate without yelling. Default 1.0.
     */
    volume?: number;
    /**
     * Called if the worklet aborts. The handle is left in a stopped
     * state; the caller should destroy and recreate it.
     */
    onError?: (error: Error) => void;
}

/** Handle for routing audio frames into the playback pipeline. */
export interface PlaybackHandle {
    /**
     * Decode and enqueue an audio frame from a remote talker.
     *
     * Decoder state is keyed by `connectionId` so multiple concurrent
     * talkers stay independent. The decoder for an unseen sender is
     * lazily created on first frame and torn down by [`removeSender`]
     * (typically driven by `floor_released`).
     *
     * Malformed packets are silently dropped — protocol-level audio
     * frame validity is enforced by the server, but we still tolerate
     * the occasional corrupt packet without poisoning the decoder.
     */
    pushFrame(connectionId: string, opusPayload: Uint8Array): void;
    /**
     * Forget a sender. Frees the decoder and tells the worklet to drop
     * any queued samples for that connection_id.
     */
    removeSender(connectionId: string): void;
    /**
     * Drop every active sender (e.g. on disconnect). Equivalent to
     * `removeSender` for each known id.
     */
    clear(): void;
    /**
     * Switch the output device the playback context renders through.
     * Resolves once the change has taken effect (or immediately on
     * browsers that don't support `AudioContext.setSinkId`). Pass
     * `undefined` or `""` to revert to the system default.
     */
    setSinkId(deviceId: string | undefined): Promise<void>;
    /**
     * Adjust the master playback gain. Takes effect on the next audio
     * block (a few ms). Out-of-range / non-finite values are ignored.
     */
    setVolume(volume: number): void;
    /**
     * Tear down the worklet and release every decoder. Idempotent.
     */
    destroy(): void;
}

/**
 * Build the playback graph and return a handle.
 *
 * Resolves once the worklet module is loaded and the audio context is
 * running. Browser autoplay policy requires the page to have a recent
 * user gesture by the time this is called — the channel screen mounts
 * directly off the user's "Connect" click, so this is satisfied in
 * practice.
 */
export async function startPlayback(opts: PlaybackOptions = {}): Promise<PlaybackHandle> {
    const ctx = new AudioContext();
    try {
        await ctx.audioWorklet.addModule(new URL("/playback-processor.js", import.meta.url));
    } catch (e) {
        await ctx.close().catch(() => undefined);
        throw new Error(`failed to load playback worklet: ${(e as Error).message}`);
    }

    /**
     * `AudioContext.setSinkId` is the only path to programmatic output
     * routing in the browser today and ships only on recent Chromium
     * builds. Probe via the prototype so feature detection works
     * before construction and after.
     */
    type SinkAware = AudioContext & { setSinkId?: (id: string) => Promise<void> };
    const trySetSinkId = async (id: string | undefined): Promise<void> => {
        const ac = ctx as SinkAware;
        if (typeof ac.setSinkId !== "function") return;
        try {
            await ac.setSinkId(id ?? "");
        } catch {
            // Stale or invalid id; stay on whatever the previous sink was.
        }
    };
    if (opts.deviceId !== undefined && opts.deviceId !== "") {
        await trySetSinkId(opts.deviceId);
    }

    const node = new AudioWorkletNode(ctx, "nettacco-playback", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
    });
    // Master gain node sits between the mixer worklet and the output
    // device so the user can boost / cut all senders together. The
    // worklet's soft-clip catches any sum that ends up past ±0.9 after
    // the multiply.
    const masterGain = ctx.createGain();
    masterGain.gain.value = opts.volume ?? 1.0;
    node.connect(masterGain);
    masterGain.connect(ctx.destination);

    // Some browsers (especially mobile) start the AudioContext in the
    // "suspended" state until a user gesture resumes it. The connect
    // flow that leads to playback always passes through one — but
    // belt-and-braces: kick it explicitly. A failed resume here is not
    // fatal; the next gesture will retry.
    if (ctx.state === "suspended") {
        ctx.resume().catch(() => undefined);
    }

    /** connection_id → decoder. Decoders are expensive to construct, so cached. */
    const decoders = new Map<string, OpusDecoder>();
    /**
     * Pending decoder construction, keyed by id. We may receive several
     * frames for a sender while the first decoder is still being built;
     * queue them on the same promise so the order on the wire is
     * preserved into the worklet.
     */
    const pendingDecoders = new Map<string, Promise<OpusDecoder>>();

    let destroyed = false;

    node.onprocessorerror = (): void => {
        opts.onError?.(new Error("playback worklet aborted"));
    };

    /**
     * Resolve (creating if necessary) the decoder for `id`. The first
     * frame for a new sender pays the construction latency; subsequent
     * frames hit the warm cache.
     */
    const getDecoder = async (id: string): Promise<OpusDecoder> => {
        const existing = decoders.get(id);
        if (existing !== undefined) return existing;
        const pending = pendingDecoders.get(id);
        if (pending !== undefined) return pending;
        const promise = OpusDecoder.create().then((dec) => {
            // The sender may have been removed while we were waiting
            // — discard the decoder rather than leaving an orphan.
            if (destroyed || !pendingDecoders.has(id)) {
                dec.destroy();
                throw new Error("decoder construction superseded");
            }
            decoders.set(id, dec);
            pendingDecoders.delete(id);
            return dec;
        });
        pendingDecoders.set(id, promise);
        return promise;
    };

    return {
        pushFrame(connectionId: string, opusPayload: Uint8Array): void {
            if (destroyed) return;
            // Copy the payload immediately — the underlying buffer
            // belongs to the transport and will be reused. The decoder
            // call below is async (first time) or microtask-deferred,
            // so we cannot rely on the original surviving.
            const payload = opusPayload.slice();
            void getDecoder(connectionId)
                .then((decoder) => {
                    if (destroyed) return;
                    let pcm: Int16Array;
                    try {
                        pcm = decoder.decode(payload);
                    } catch {
                        // Malformed or out-of-sync packet. The Opus
                        // decoder is robust to this — drop the frame.
                        return;
                    }
                    node.port.postMessage(
                        { kind: "frame", connectionId, samples: pcm },
                        [pcm.buffer],
                    );
                })
                .catch(() => undefined);
        },
        removeSender(connectionId: string): void {
            if (destroyed) return;
            const dec = decoders.get(connectionId);
            if (dec !== undefined) {
                dec.destroy();
                decoders.delete(connectionId);
            }
            pendingDecoders.delete(connectionId);
            node.port.postMessage({ kind: "remove", connectionId });
        },
        clear(): void {
            if (destroyed) return;
            for (const dec of decoders.values()) dec.destroy();
            decoders.clear();
            pendingDecoders.clear();
            node.port.postMessage({ kind: "clear" });
        },
        async setSinkId(deviceId: string | undefined): Promise<void> {
            if (destroyed) return;
            await trySetSinkId(deviceId);
        },
        setVolume(volume: number): void {
            if (destroyed) return;
            if (!Number.isFinite(volume) || volume < 0) return;
            masterGain.gain.value = volume;
        },
        destroy(): void {
            if (destroyed) return;
            destroyed = true;
            for (const dec of decoders.values()) dec.destroy();
            decoders.clear();
            pendingDecoders.clear();
            node.port.onmessage = null;
            node.disconnect();
            masterGain.disconnect();
            ctx.close().catch(() => undefined);
        },
    };
}
